import { Box, Button, Checkbox, ListItem, Stack, Text, UnorderedList } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isNotNull } from '@/utils/helpers'

import { useHasuraContext } from '@/client/context/hasura'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { POSTS_QUERY } from '@/client/services/hasura'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import type { SerializedRichText } from '@/client/services/richText/types'
import {
  canEmailUsersInGlobalChannel,
  canNotifyUsersInGlobalChannel,
  QUESTION_TYPES,
  type QuestionType,
} from '@/client/services/worlds'

import usePostComposer from '@/components/Community/Post/internal/usePostComposer'
import type { ChannelType } from '@/components/Community/Post/types'
import Spinner from '@/components/Spinner'
import Alert from '@/components/UI/Alert'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import RichTextEditorSimple from '@/components/UI/RichTextEditor/RichTextEditorSimple/RichTextEditorSimple'
import type { RichTextEditorSimpleRef } from '@/components/UI/RichTextEditor/RichTextEditorSimple/types'
import type { UserDetails } from '@/components/Worlds/types'
import AvatarWithName from '@/components/Worlds/UI/Avatar/AvatarWithName'

import {
  QUESTION_TYPE_TO_ICON,
  QUESTION_TYPE_TO_LABEL,
  QUESTION_TYPE_TO_RICH_TEXT_TEMPLATE,
} from './constants'

const SHOULD_REQUEST_LIVE_HELP_DEFAULT = true
const DEFAULT_QUESTION_TYPE = 'problem-help'

const CHANNEL_TYPE_TO_GUIDELINES_AGREEMENT: Record<ChannelType, null | (() => JSX.Element)> = {
  question: () => (
    <Text variant="supporting" color="fg.muted">
      By posting, you agree that you&apos;re here to learn! Pressuring someone to do your homework
      for you may result in the suspension of your account.
    </Text>
  ),
  discussion: null,
  announcement: null,
}

const QuestionTypeItem = ({
  type,
  onClick,
  isActive,
}: {
  type: QuestionType
  onClick: () => void
  isActive: boolean
}): JSX.Element => (
  <Button
    variant="outline"
    width="100%"
    height="fit-content"
    leftIcon={<FontAwesomeIcon icon={QUESTION_TYPE_TO_ICON[type]} />}
    display="flex"
    flexDir="row"
    justifyContent="left"
    textAlign="left"
    whiteSpace="break-spaces"
    overflowWrap="break-word"
    onClick={onClick}
    isActive={isActive}
    _active={{
      border: '2px solid',
      borderColor: 'primary.focusRing',
      bgColor: 'primary.subtle',
    }}
  >
    {QUESTION_TYPE_TO_LABEL[type]}
  </Button>
)

export default function EditPostModal({
  channel,
  postUser,
  postId = null,
  initialBody,
  initialShouldRequestLiveHelp = SHOULD_REQUEST_LIVE_HELP_DEFAULT,
  open,
  onDiscard,
  onClose,
}: {
  channel: { id: number; title: string; type: ChannelType }
  postUser: Pick<
    UserDetails,
    | 'id'
    | 'displayName'
    | 'avatarUrl'
    | 'username'
    | 'activeAnniversary'
    | 'roles'
    | 'keyClubDistrictName'
    | 'collegeInfo'
  >
  postId?: number | null
  initialBody?: SerializedRichText | null
  initialShouldRequestLiveHelp?: boolean
  open: boolean
  onDiscard: (body: SerializedRichText) => void
  onClose: () => void
}): JSX.Element {
  const user = useUser()
  const router = useRouter()
  const hasuraContext = useHasuraContext()

  const isUpdating = postId != null

  const editorRef = useRef<RichTextEditorSimpleRef>(null)

  const [body, setBody] = useState<SerializedRichText>(initialBody ?? EMPTY_RICH_TEXT_VALUE)
  const [shouldRequestLiveHelp, setShouldRequestLiveHelp] = useState(initialShouldRequestLiveHelp)
  const [shouldNotifyUsers, setShouldNotifyUsers] = useState(false)
  const [shouldEmailUsers, setShouldEmailUsers] = useState(false)
  const [blockedError, setBlockedError] = useState<string | null>(null)
  const [questionType, setQuestionType] = useState<QuestionType>(DEFAULT_QUESTION_TYPE)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  useEffect(() => {
    setBody(initialBody ?? EMPTY_RICH_TEXT_VALUE)
  }, [initialBody])

  const canNotifyUsers = canNotifyUsersInGlobalChannel({ user })

  const canEmailUsers = canEmailUsersInGlobalChannel({ user })

  const guidelinesAgreement = useMemo(
    () => CHANNEL_TYPE_TO_GUIDELINES_AGREEMENT[channel.type],
    [channel]
  )

  const MemoizedRichTextEditor = useCallback(
    () => (
      <RichTextEditorSimple
        ref={editorRef}
        name="EditPost"
        initialValue={
          initialBody ??
          (questionType != null
            ? QUESTION_TYPE_TO_RICH_TEXT_TEMPLATE[questionType](user)
            : EMPTY_RICH_TEXT_VALUE)
        }
        onChange={setBody}
      />
    ),
    [initialBody, questionType, user]
  )

  const {
    handleCreatePost,
    isCreatingPost,
    fileUploadStatus,
    setFileUploadStatus,
    isBodyEmpty,
    RequestNotificationsModal,
  } = usePostComposer({
    channel,
    body,
    setBody,
    editorRef,
    onPostBlocked: message => setBlockedError(message),
    shouldNotifyUsers,
    shouldEmailUsers,
    onClose,
  })

  const handleClose = useCallback(() => {
    setFileUploadStatus(null)
    editorRef.current?.clear()
    setBody(EMPTY_RICH_TEXT_VALUE)
    setShouldRequestLiveHelp(SHOULD_REQUEST_LIVE_HELP_DEFAULT)
    setBlockedError(null)
    if (isBodyEmpty) {
      onClose()
    } else {
      onDiscard(body)
    }
  }, [isBodyEmpty, onClose, body, onDiscard, setFileUploadStatus])

  const [handleEditPost, isEditingPost] = useAPIWrapper(
    async () => {
      if (!isUpdating) {
        return
      }

      const editPostResult = await RPC.editPost({
        postId,
        channelId: channel.id,
        body,
      })

      setFileUploadStatus(null)

      if (editPostResult?.status === 'blocked') {
        setBlockedError(editPostResult.message)
        return
      }

      if (editPostResult?.status === 'suspended') {
        router.reload()
        return
      }

      editorRef.current?.clear()
      onClose()

      await hasuraContext?.apollo.refetchQueries({
        include: [POSTS_QUERY],
      })
    },
    [
      isUpdating,
      postId,
      channel.id,
      body,
      onClose,
      hasuraContext?.apollo,
      router,
      setFileUploadStatus,
    ],
    'Edit Post'
  )

  return (
    <>
      <ModalV3
        size="xl"
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        title="Create Post"
        body={() => (
          <>
            <Text>Please confirm that you would like to:</Text>
            <UnorderedList>
              {shouldNotifyUsers ? (
                <ListItem>
                  Send an <b>in-platform notification</b> to all channel members about this post.
                </ListItem>
              ) : null}
              {shouldEmailUsers ? (
                <ListItem>
                  Send an <b>email notification</b> to all channel members about this post.
                </ListItem>
              ) : null}
            </UnorderedList>
          </>
        )}
        leftFooterContent={() => (
          <Button variant="ghost" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
        )}
        rightFooterContent={() => (
          <Button
            onClick={() =>
              handleCreatePost({
                liveHelpInfo: {
                  questionType,
                  requestZoomHelp: shouldRequestLiveHelp,
                },
              })
            }
            disabled={isCreatingPost}
          >
            Confirm
          </Button>
        )}
      />
      <ModalV3
        size="2xl"
        open={open}
        onClose={handleClose}
        title={`${isUpdating ? 'Edit Post' : 'New Post'}`}
        body={() => (
          <Stack direction="column" spacing="24px">
            {blockedError == null ? null : (
              <Alert variant="error">
                <Text>{blockedError}</Text>
              </Alert>
            )}

            {fileUploadStatus == null ? null : (
              <Alert variant="warning" excludeIcon>
                <Stack direction="row" align="center" spacing="8px">
                  <Spinner size="sm" thickness="2px" />
                  <Text>
                    Uploading files... ({fileUploadStatus.uploaded}/{fileUploadStatus.total})
                  </Text>
                </Stack>
              </Alert>
            )}

            <Stack direction="row" align="center" justify="space-between">
              <AvatarWithName user={postUser} />
            </Stack>

            {channel.type === 'question' && initialBody == null ? (
              <Stack>
                <Text fontWeight={600}>What do you need help with today?</Text>
                <Stack>
                  {QUESTION_TYPES.map(type => (
                    <QuestionTypeItem
                      key={type}
                      type={type}
                      isActive={type === questionType}
                      onClick={() => setQuestionType(type)}
                    />
                  ))}
                </Stack>
              </Stack>
            ) : null}

            {initialBody != null ||
            channel.type !== 'question' ||
            (channel.type === 'question' && isNotNull(questionType)) ? (
              <MemoizedRichTextEditor />
            ) : null}

            {channel.type === 'question' &&
            questionType != null &&
            questionType !== 'topic-review' ? (
              <Stack>
                <Text fontWeight={600}>
                  Would you like to request immediate help from a tutor over Zoom?
                </Text>
                <Box>
                  <Checkbox
                    isChecked={shouldRequestLiveHelp}
                    onChange={e => setShouldRequestLiveHelp(e.target.checked)}
                  >
                    Yes, request Zoom help!
                  </Checkbox>
                </Box>
              </Stack>
            ) : null}

            {canNotifyUsers && channel.type !== 'question' ? (
              <Stack>
                <Text fontWeight={600}>
                  Would you like to send an in-platform notification to all channel members about
                  this post?
                </Text>
                <Box>
                  <Checkbox
                    isChecked={shouldNotifyUsers}
                    onChange={e => {
                      setShouldNotifyUsers(e.target.checked)
                      if (!e.target.checked) {
                        setShouldEmailUsers(false)
                      }
                    }}
                  >
                    Yes, notify all members!
                  </Checkbox>
                </Box>
              </Stack>
            ) : null}

            {!isUpdating && canEmailUsers && shouldNotifyUsers ? (
              <Stack>
                <Text fontWeight={600}>
                  Would you like to send an email notification to all channel members about this
                  post?
                </Text>
                <Box>
                  <Checkbox
                    isChecked={shouldEmailUsers}
                    onChange={e => setShouldEmailUsers(e.target.checked)}
                  >
                    Yes, email all members!
                  </Checkbox>
                </Box>
              </Stack>
            ) : null}

            {channel.type === 'question' && questionType == null ? null : guidelinesAgreement?.()}
          </Stack>
        )}
        rightFooterContent={() => (
          <Stack direction="row" spacing="8px">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={
                isUpdating
                  ? handleEditPost
                  : shouldNotifyUsers || shouldEmailUsers
                    ? () => setShowConfirmationModal(true)
                    : () =>
                        handleCreatePost({
                          liveHelpInfo: {
                            questionType,
                            requestZoomHelp: shouldRequestLiveHelp,
                          },
                        })
              }
              isDisabled={isBodyEmpty || isEditingPost}
            >
              {isUpdating ? 'Save' : 'Post'}
            </Button>
          </Stack>
        )}
      />
      <RequestNotificationsModal />
    </>
  )
}
